import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {CategorizedSelectList} from '../categorized-select-list/categorized-select-list';
import type {Category} from '../categorized-select-list/categorized-select-list';
import type {DripEdition} from '../drip-sequence-list-item/drip-sequence-list-item';
import {DripSequenceThumbnail} from '../drip-sequence-thumbnail/drip-sequence-thumbnail';
import type {DripSequenceForThumbnail as DripSequence} from '../drip-sequence-thumbnail/drip-sequence-thumbnail';
import {AddNewCard} from '../edition-thumbnail-list/add-new-card';
import {EditionThumbnailCard} from '../edition-thumbnail-list/edition-thumbnail-card';
import type {Edition} from '../edition-thumbnail-list/types';
import {StackLegacy} from '../layout/stack/stack-legacy';
import {Link} from '../link/link';

type MarketingOutreachItem = {
    id: string;
    createNew?: {
        name: string;
        height?: number;
    };
    edition?: Edition & {
        comparison?: any;
        htmlUrlPath?: string;
    };
    dripSequence?: DripSequence & {
        editions: DripEdition[];
    };
    disabledReasons?: string[];
};

type Props = {
    categorizedLists: Category<MarketingOutreachItem>[] | null | undefined;
    initialSelectedCategoryId?: string;
    selectedItemId: string | null | undefined;
    setSelectedItemId: (id?: string | null | undefined) => void;
    showEditionTypeIcon?: boolean;
    isFiltering?: boolean;
    isLoading?: boolean;
    setSelectedTab?: (
        selectedTab: 'recentMessages' | 'companyTemplates' | 'aiEmailTemplates'
    ) => void;
};

export function MarketingOutreachSelectList(props: Props) {
    return (
        <CategorizedSelectList
            categorizedLists={props.categorizedLists}
            // @ts-expect-error
            renderItem={(item) => {
                if (item.edition) {
                    return (
                        <EditionThumbnailCard
                            key={item.id}
                            edition={item.edition}
                            selectedId={props.selectedItemId}
                            onSelect={() => props.setSelectedItemId(item.id)}
                            displayIcon={props.showEditionTypeIcon}
                            unsetCardWidth={true}
                            disabledStatusDetail={
                                <StackLegacy spacing={8}>
                                    <div>This message isn’t ready to send:</div>
                                    {item.disabledReasons ? (
                                        <ul>
                                            {item.disabledReasons.map((reason) => (
                                                <li key={reason}>• {reason}</li>
                                            ))}
                                        </ul>
                                    ) : null}
                                    {item.edition && item.edition.htmlUrlPath && (
                                        <Link
                                            // @ts-expect-error
                                            as={RoutedLink}
                                            variant='button-primary'
                                            to={`${item.edition.htmlUrlPath}/editor`}
                                            newTab={true}
                                        >
                                            Go to edition
                                        </Link>
                                    )}
                                </StackLegacy>
                            }
                            isDisabled={Boolean(
                                item.disabledReasons && item.disabledReasons.length
                            )}
                        />
                    );
                } else if (item.dripSequence) {
                    return (
                        <DripSequenceThumbnail
                            key={item.id}
                            dripSequence={item.dripSequence}
                            dripEditions={item.dripSequence.editions}
                            onClick={() => props.setSelectedItemId(item.id)}
                            isSelected={props.selectedItemId === item.id}
                        />
                    );
                } else if (item.createNew) {
                    return (
                        <AddNewCard
                            key={item.id}
                            text={item.createNew.name}
                            onClick={() => props.setSelectedItemId(item.id)}
                            isSelected={props.selectedItemId === item.id}
                            isSelectable={true}
                            height={item.createNew.height}
                        />
                    );
                } else {
                    return null;
                }
            }}
            isLoading={props.isLoading}
            isFiltering={props.isFiltering}
            initialSelectedCategoryId={props.initialSelectedCategoryId}
            onSelectCategory={(category) => {
                if (category && category.collection) {
                    if (props.setSelectedTab) {
                        if (category.id === 'templates') {
                            props.setSelectedTab('companyTemplates');
                        } else {
                            props.setSelectedTab('recentMessages');
                        }
                    }

                    const firstItem = category.collection[0];
                    props.setSelectedItemId(
                        firstItem && !firstItem.disabledReasons
                            ? // @ts-expect-error
                              category.collection[0].id
                            : undefined
                    );
                }

                if (category.id === 'ai-email-template' && props.setSelectedTab) {
                    props.setSelectedTab('aiEmailTemplates');
                }
            }}
        />
    );
}
